import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GitCompareArrows, ArrowUpRight } from "lucide-react";
import { getCompany } from "../../lib/api";
import { StaggerGrid, StaggerItem } from "../Motion";
import { SkeletonGrid } from "../LoadingSkeleton";

const scoreColor = (v) => (v >= 80 ? "#10B981" : v >= 65 ? "#84CC16" : v >= 50 ? "#F59E0B" : v >= 35 ? "#FB923C" : "#F43F5E");

// Portfolio Intelligence + Continue Research combined: fetches each unique
// held company via the existing /companies/:ticker endpoint (frontend-only,
// no new backend routes) and shows its real LONGVIEW score alongside quick
// research shortcuts. Silently omits any company whose lookup fails.
export const ResearchGrid = ({ tickers }) => {
  const [companies, setCompanies] = useState(null);
  const key = tickers.join(",");

  useEffect(() => {
    let active = true;
    setCompanies(null);
    Promise.all(tickers.map((t) => getCompany(t).catch(() => null))).then((results) => {
      if (active) setCompanies(results.filter(Boolean));
    });
    return () => { active = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (companies === null) return <SkeletonGrid count={tickers.length} blockClassName="h-40" cols="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" />;
  if (companies.length === 0) return null;

  return (
    <StaggerGrid data-testid="portfolio-research-grid" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {companies.map((c) => (
        <StaggerItem key={c.ticker}>
          <div className="lv-panel lv-panel-hover p-4 transition-transform duration-200 hover:-translate-y-0.5">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="font-mono text-xs font-bold text-white">{c.ticker}</span>
              {c.scores?.risk_level && <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">{c.scores.risk_level} Risk</span>}
            </div>
            <p className="truncate text-sm text-zinc-300">{c.name}</p>
            {c.scores?.business_quality != null && (
              <div className="mt-3 flex items-center gap-2">
                <span className="font-mono text-lg font-bold tabular-nums" style={{ color: scoreColor(c.scores.business_quality) }}>{c.scores.business_quality}</span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">LONGVIEW Score</span>
              </div>
            )}
            <div className="mt-3 flex items-center gap-2 border-t border-white/[0.06] pt-3">
              <Link to={`/company/${c.ticker}`} data-testid={`portfolio-research-view-${c.ticker.toLowerCase()}`} className="lv-press flex flex-1 items-center justify-center gap-1 rounded-md border border-white/[0.1] py-1.5 font-mono text-[11px] text-zinc-300 transition-colors hover:border-amber-500/40 hover:text-amber-200">
                <ArrowUpRight className="h-3 w-3" /> View
              </Link>
              <Link to={`/compare?a=${c.ticker}`} data-testid={`portfolio-research-compare-${c.ticker.toLowerCase()}`} className="lv-press flex flex-1 items-center justify-center gap-1 rounded-md border border-white/[0.1] py-1.5 font-mono text-[11px] text-zinc-300 transition-colors hover:border-amber-500/40 hover:text-amber-200">
                <GitCompareArrows className="h-3 w-3" /> Compare
              </Link>
            </div>
          </div>
        </StaggerItem>
      ))}
    </StaggerGrid>
  );
};
